import { useMemo, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import i18n from "../../../language/i18n";
import { EmptyState, SelectionTab as _SelectionTab } from "../../../library";
import {
  buildTrendChartModel,
  isDrawableSource,
  modelHasTrend,
  TrendSource,
} from "../../../utilization/ScoreTrendModel";
import { learnerTrendChartStyles } from "./LearnerTrendChartStyles";
// Side-effect import: registers the chart.js elements/scales this Line chart needs.
import "../../../utilization/ChartSetup";

export type { TrendPoint, TrendSource } from "../../../utilization/ScoreTrendModel";

const SelectionTab: any = _SelectionTab;

const ALL_SOURCE_ID = "ALL";

// "Öğrencinin zaman içindeki gelişimi" — the learner's own score history, one line per assessment
// source. The staff console has the same chart on its branch report; the pure model behind both
// lives in utilization/ScoreTrendModel so the two consoles cannot disagree about what a trend is.
// This component is the app-web presentation of it, in this app's design language.

interface LearnerTrendChartProps {
  title: string;
  subtitle?: string;
  sources: TrendSource[];
  emptyText: string;
  /** The learner's UI language, for axis and tooltip dates (the context's persisted "TR"/"EN"). */
  language: string;
  height?: number;
  /**
   * The chart's own one-of-N source strip.
   *
   * <p>Opt-out rather than removed. One hoisted "Kaynak" row now governs this chart, the outcome tree
   * and the güçlü/zayıf cards together — the report used to carry TWO controls for one axis, and set
   * to different values the two halves of it showed different things. Callers that do not hoist a row
   * keep the strip.
   *
   * <p>The strip is a ROUND one-of-N: it can show one source or all, never "şube + bireysel without
   * the exams". That is precisely why the hoisted row is a square multi-select instead.
   */
  showSourceTabs?: boolean;
  /**
   * Whether anything OUTSIDE the legend names the source, which is what `buildTrendChartModel`
   * actually asks. The strip used to be the only such thing, so the two were ONE boolean — and
   * hiding the strip would silently flip the legend back to naming sources instead of scopes.
   * A hoisted Kaynak row names the source too, so a caller that replaces the strip with that row
   * says so here rather than letting the chart infer "nobody names it".
   */
  sourceNamedElsewhere?: boolean;
}

interface TrendTooltipMeta {
  pointLabels: string[];
  timestamps: number[];
  language: string;
}

/**
 * Chart.js external tooltip rendered as a frosted HTML popover — a canvas tooltip cannot wrap a long
 * assignment name and cannot take the glass treatment.
 *
 * <p>Slot names and dates are read from a REF the component refreshes every render, never from the
 * chart instance: react-chartjs-2 forwards its ref inside a mount-only effect, so anything stashed
 * on the instance freezes at the first render and the tooltip would keep naming the FIRST timeline's
 * points after a chip or the time filter changed the axis.
 */
const makeTrendTooltipRenderer =
  (metaRef: { current: TrendTooltipMeta }) => (context: any) => {
    const { chart, tooltip } = context;
    const parent = chart?.canvas?.parentNode as HTMLElement | null;
    if (!parent) return;

    let element = parent.querySelector<HTMLDivElement>(".ltc-tooltip");
    if (!element) {
      element = document.createElement("div");
      element.className = "ltc-tooltip";
      parent.appendChild(element);
    }

    if (!tooltip || tooltip.opacity === 0) {
      element.style.opacity = "0";
      return;
    }

    const index = tooltip.dataPoints?.[0]?.dataIndex;
    const { pointLabels, timestamps, language } = metaRef.current;
    const title = index != null ? pointLabels[index] : "";
    const timestamp = index != null ? timestamps[index] : null;

    // textContent throughout — never innerHTML. These strings include learner-authored assignment
    // names, which must never be parsed as markup.
    element.replaceChildren();
    if (title) {
      const titleNode = document.createElement("div");
      titleNode.className = "ltc-tip-title";
      titleNode.textContent = title;
      element.appendChild(titleNode);
    }
    if (timestamp != null) {
      const dateNode = document.createElement("div");
      dateNode.className = "ltc-tip-date";
      dateNode.textContent = new Date(timestamp).toLocaleDateString(language, {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      element.appendChild(dateNode);
    }

    const rows = document.createElement("div");
    rows.className = "ltc-tip-rows";
    (tooltip.dataPoints || []).forEach((dataPoint: any) => {
      const row = document.createElement("div");
      row.className = "ltc-tip-row";

      const dot = document.createElement("span");
      dot.className = "ltc-tip-dot";
      dot.style.background = dataPoint.dataset?.borderColor || "transparent";
      row.appendChild(dot);

      const label = document.createElement("span");
      label.className = "ltc-tip-label";
      label.textContent = dataPoint.dataset?.label || "";
      row.appendChild(label);

      const value = document.createElement("span");
      value.className = "ltc-tip-value";
      const raw = dataPoint.raw;
      // A gap reads as "—". Showing 0% would report a collapse the learner never had.
      value.textContent = raw == null ? "—" : `%${Math.round(Number(raw))}`;
      row.appendChild(value);

      rows.appendChild(row);
    });
    element.appendChild(rows);

    element.style.opacity = "1";
    element.style.left = `${tooltip.caretX}px`;
    element.style.top = `${tooltip.caretY}px`;
    element.style.transform = "translate(-50%, calc(-100% - 12px))";
  };

const LearnerTrendChart = ({
  title,
  subtitle,
  sources,
  emptyText,
  language,
  height = 260,
  showSourceTabs = true,
  sourceNamedElsewhere,
}: LearnerTrendChartProps) => {
  const [selectedSourceId, setSelectedSourceId] = useState<string>(ALL_SOURCE_ID);

  const drawableSources = useMemo(
    () => (sources || []).filter(isDrawableSource),
    [sources],
  );

  // A chip whose source vanished (ders switched, window narrowed) must not strand the chart on an
  // empty selection.
  const activeSourceId =
    selectedSourceId !== ALL_SOURCE_ID &&
    !drawableSources.some((source) => source.id === selectedSourceId)
      ? ALL_SOURCE_ID
      : selectedSourceId;

  const visibleSources = useMemo(
    () =>
      activeSourceId === ALL_SOURCE_ID
        ? drawableSources
        : drawableSources.filter((source) => source.id === activeSourceId),
    [drawableSources, activeSourceId],
  );

  // One fact drives both the chip strip and how the legend labels a lone series: with a single
  // drawable source there is no strip, so the legend is the only thing naming the source and must
  // keep doing so. Deriving both from this const is what stops them drifting apart.
  const sourceChipsVisible = showSourceTabs && drawableSources.length > 1;
  // Split from the flag above: one answers "is the strip on screen", the other "is the source named
  // anywhere outside the legend". They coincided only while the strip was the sole namer.
  const sourceIsNamedOutsideLegend = sourceNamedElsewhere ?? sourceChipsVisible;

  const trendModel = useMemo(
    () => buildTrendChartModel(visibleSources, sourceIsNamedOutsideLegend),
    [visibleSources, sourceIsNamedOutsideLegend],
  );

  const tooltipMetaRef = useRef<TrendTooltipMeta>({
    pointLabels: [],
    timestamps: [],
    language,
  });
  tooltipMetaRef.current = {
    pointLabels: trendModel.pointLabels,
    timestamps: trendModel.timestamps,
    language,
  };
  const renderTooltip = useMemo(() => makeTrendTooltipRenderer(tooltipMetaRef), []);

  // Formatted here rather than in the model, so the model stays locale- and Date-free and therefore
  // deterministically testable.
  const axisLabels = useMemo(() => {
    const { timestamps } = trendModel;
    if (timestamps.length === 0) return [];
    const spansYears =
      new Date(timestamps[0]).getFullYear() !==
      new Date(timestamps[timestamps.length - 1]).getFullYear();
    return timestamps.map((timestamp) =>
      new Date(timestamp).toLocaleDateString(language, {
        day: "numeric",
        month: "short",
        ...(spansYears ? { year: "numeric" } : {}),
      }),
    );
  }, [trendModel, language]);

  const chartDatasets = useMemo(
    () =>
      trendModel.datasets.map((dataset) => ({
        label: dataset.label,
        data: dataset.data,
        borderColor: dataset.color,
        backgroundColor: dataset.filled ? `${dataset.color}1F` : "transparent",
        fill: dataset.filled,
        borderWidth: dataset.dashed ? 1.75 : 2,
        ...(dataset.dashed ? { borderDash: [6, 4] } : {}),
        tension: 0.3,
        pointRadius: dataset.dashed ? 2.5 : 3.5,
        pointHoverRadius: dataset.dashed ? 4.5 : 5.5,
        pointBackgroundColor: dataset.color,
        pointBorderColor: "#FFFFFF",
        pointBorderWidth: dataset.dashed ? 1 : 1.5,
        // Gaps are spanned, never zero-filled: an unassessed period is a gap, not a 0%.
        spanGaps: true,
      })),
    [trendModel],
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index" as const, intersect: false },
      plugins: {
        legend: {
          position: "bottom" as const,
          labels: {
            usePointStyle: true,
            boxWidth: 8,
            // With usePointStyle, Chart.js sizes the marker from boxHeight (`boxHeight * √2 / 2`),
            // not boxWidth — the default (= font size) draws a disc that overflows its own box.
            boxHeight: 6,
            padding: 14,
            font: { family: "Quicksand", size: 12 },
          },
        },
        tooltip: { enabled: false, external: renderTooltip },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            autoSkip: true,
            maxRotation: 0,
            font: { family: "Quicksand", size: 11 },
          },
        },
        y: {
          min: 0,
          max: 100,
          grid: { color: "rgba(15, 72, 89, 0.06)" },
          ticks: {
            stepSize: 20,
            font: { family: "Quicksand", size: 11 },
            callback: (value: any) => `%${value}`,
          },
        },
      },
    }),
    [renderTooltip],
  );

  const sourceOptionList = useMemo(
    () => [
      { id: ALL_SOURCE_ID, title: i18n.t("trend_source_all") },
      ...drawableSources.map((source) => ({ id: source.id, title: source.label })),
    ],
    [drawableSources],
  );

  // A single point is a dot, not a trend; an axis drawn around no data reads as "scored nothing"
  // rather than "nothing assessed yet".
  const hasTrend = modelHasTrend(trendModel);

  return (
    <div className="ltc-card">
      <style>{learnerTrendChartStyles}</style>
      <div className="ltc-head">
        <p className="ltc-title">{title}</p>
        {subtitle ? <p className="ltc-subtitle">{subtitle}</p> : null}
      </div>

      {/* The chips stay up even when the CURRENT selection draws nothing — gating them on hasTrend
          would remove the only control that could get the learner back to "Tümü". */}
      {sourceChipsVisible ? (
        <div className="ltc-tabs">
          <SelectionTab
            optionList={sourceOptionList}
            selectedId={activeSourceId}
            handleSelectOnClick={(id: string) => setSelectedSourceId(String(id))}
          />
        </div>
      ) : null}

      {hasTrend ? (
        <div className="ltc-canvas" style={{ height }}>
          <Line
            options={options}
            data={{ labels: axisLabels, datasets: chartDatasets }}
          />
        </div>
      ) : (
        <EmptyState compact icon="chartBar" title={emptyText} />
      )}
    </div>
  );
};

export default LearnerTrendChart;
